import * as Yup from "yup";
import { InputType } from "../../helper/FormHelper.jsx";
import { cloneRankValue, DEFAULT_RANK_VALUE } from "./skillRankEdit";

const defaultRanks = cloneRankValue(DEFAULT_RANK_VALUE);

const FormControl = [
  {
    size: { xs: 12, sm: 6 },
    variant: InputType.INPUT,
    props: { id: "name_zh", name: "name_zh", label: "中文名稱 *", type: "text" },
  },
  {
    size: { xs: 12, sm: 6 },
    variant: InputType.INPUT,
    props: { id: "name_en", name: "name_en", label: "英文名稱 *", type: "text" },
  },
  {
    size: { xs: 12, sm: 6 },
    variant: InputType.INPUT,
    props: { id: "code", name: "code", label: "技能代碼 *", type: "text" },
  },
  {
    size: { xs: 12, sm: 6 },
    variant: InputType.SINGLE_SELECTOR,
    props: { id: "type", name: "type", label: "類型" },
    additionInfo: {
      options: [
        { value: "normal", label: "一般" },
        { value: "fit_exam", label: "體能測試" },
      ],
    },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: { id: "fitness_type", name: "fitness_type", label: "體能類型代碼", type: "text" },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: { id: "fitness_type_zh", name: "fitness_type_zh", label: "體能類型中文", type: "text" },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: { id: "fitness_type_en", name: "fitness_type_en", label: "體能類型英文", type: "text" },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: { id: "unit", name: "unit", label: "單位", type: "text" },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: { id: "decimal_place", name: "decimal_place", label: "小數位", type: "number" },
  },
  {
    size: { xs: 12, sm: 4 },
    variant: InputType.INPUT,
    props: {
      id: "dashboard_position",
      name: "dashboard_position",
      label: "儀表板順序",
      type: "number",
    },
  },
  {
    size: { xs: 12 },
    variant: InputType.TEXTAREA,
    props: { id: "import_setting", name: "import_setting", label: "輸入設定 JSON" },
  },
  {
    size: { xs: 12 },
    variant: InputType.SWITCH,
    props: { id: "support_ipad", name: "support_ipad", label: "支援 iPad" },
  },
];

const Schema = Yup.object().shape({
  name_zh: Yup.string().required("請輸入中文名稱"),
  name_en: Yup.string().required("請輸入英文名稱"),
  code: Yup.string().required("請輸入技能代碼"),
  type: Yup.string().oneOf(["normal", "fit_exam"]).required("請選擇類型"),
  decimal_place: Yup.number().typeError("請輸入數字").required("請輸入小數位"),
  dashboard_position: Yup.number().typeError("請輸入數字").required("請輸入儀表板順序"),
  import_setting: Yup.string().test("json", "請輸入有效的 JSON", (value) => {
    if (!value || !String(value).trim()) {
      return true;
    }
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return false;
    }
  }),
});

const FormikInstance = {
  initialValues: {
    name_zh: "",
    name_en: "",
    code: "",
    type: "normal",
    projects: [],
    fitness_type: "",
    fitness_type_zh: "",
    fitness_type_en: "",
    unit: "",
    decimal_place: 0,
    dashboard_position: 99,
    import_setting: "",
    support_ipad: false,
    rank_direction: defaultRanks.rank_direction,
    skill_rank_male: defaultRanks.skill_rank_male,
    skill_rank_female: defaultRanks.skill_rank_female,
  },
  validateOnMount: true,
  validationSchema: Schema,
};

export function buildSkillPayload(values) {
  const rawSetting = String(values.import_setting || "").trim();
  return {
    name_zh: values.name_zh,
    name_en: values.name_en,
    code: values.code,
    type: values.type,
    projects: Array.isArray(values.projects) ? values.projects : [],
    fitness_type: values.fitness_type || "",
    fitness_type_zh: values.fitness_type_zh || "",
    fitness_type_en: values.fitness_type_en || "",
    unit: values.unit || "",
    decimal_place: values.decimal_place === "" ? 0 : Number(values.decimal_place),
    dashboard_position: values.dashboard_position === "" ? 99 : Number(values.dashboard_position),
    import_setting: rawSetting ? JSON.parse(rawSetting) : {},
    support_ipad: Boolean(values.support_ipad),
    rank_direction: values.rank_direction === "asc" ? "asc" : "desc",
    skill_rank_male: values.skill_rank_male || [],
    skill_rank_female: values.skill_rank_female || [],
  };
}

export function recordToFormValues(record) {
  const ranks = cloneRankValue({
    rank_direction: record.rank_direction,
    skill_rank_male: record.skill_rank_male,
    skill_rank_female: record.skill_rank_female,
  });
  let importSetting = "";
  if (record.import_setting && typeof record.import_setting === "object") {
    importSetting = JSON.stringify(record.import_setting, null, 2);
  } else if (typeof record.import_setting === "string") {
    importSetting = record.import_setting;
  }
  return {
    ...FormikInstance.initialValues,
    name_zh: record.name_zh || "",
    name_en: record.name_en || "",
    code: record.code || "",
    type: record.type || "normal",
    projects: Array.isArray(record.projects) ? record.projects : [],
    fitness_type: record.fitness_type || "",
    fitness_type_zh: record.fitness_type_zh || "",
    fitness_type_en: record.fitness_type_en || "",
    unit: record.unit || "",
    decimal_place: record.decimal_place ?? 0,
    dashboard_position: record.dashboard_position ?? 99,
    import_setting: importSetting,
    support_ipad: Boolean(record.support_ipad),
    rank_direction: ranks.rank_direction,
    skill_rank_male: ranks.skill_rank_male,
    skill_rank_female: ranks.skill_rank_female,
  };
}

const FormContent = {
  Schema,
  FormControl,
  FormikInstance,
};

export default FormContent;
